import * as webdriverio from 'webdriverio';
import { makeSDK } from '@applitools/eyes-sdk-core';
import { TabServiceMock } from './services/tabServiceMock';
import { portSelectorMock } from './services/portSelector';
import { getLogger } from '../commons/logger';
import { MobileStepActionUtils } from './utils/mobileStepActionUtils';
import { isDebuggerConnected } from '../commons/detectDebugger';
import { getSessionPlayer } from '../commons/getSessionPlayerRequire';
import { stepActionRegistrar } from './stepActions/stepActionRegistrar';
import { FrameLocatorMock } from './services/mobileFrameLocatorMock';
import { PlaybackTimeoutCalculator } from './services/playbackTimeoutCalculator';
import type { StepActionFactory } from 'clickim/src/background/stepActions/stepActionFactory';
import type { SessionPlayer } from 'clickim/src/background/session/sessionPlayer';

const logger = getLogger('appium-test-player');

type WebdriverIO = typeof webdriverio;
export class AppiumTestPlayer {
    public readonly sessionPlayer: SessionPlayer;
    private readonly stepActionFactory: StepActionFactory;
    private readonly playbackTimeoutCalculator: PlaybackTimeoutCalculator;

    constructor(
        public readonly id: number,
        userParamsData: object,
        shouldMonitorPerformance: boolean | undefined,
        public readonly driver: WebdriverIO = webdriverio,
        testRetryCount: number | undefined = undefined,
        previousTestResultId: string | undefined = undefined,
    ) {
        const { SessionPlayer, commonConstants, StepActionFactory, MobileLocateElementPlayer } = getSessionPlayer();

        // @ts-expect-error TODO: We are using webdriverio directly, which can cause bad errors in here
        const stepActionUtils = new MobileStepActionUtils(this.driver);
        this.stepActionFactory = new StepActionFactory(stepActionUtils);
        // @ts-expect-error TODO: We are using webdriverio directly, which can cause bad errors in here
        stepActionRegistrar(this.driver, this.stepActionFactory);

        this.playbackTimeoutCalculator = new PlaybackTimeoutCalculator(isDebuggerConnected());

        this.sessionPlayer = new SessionPlayer(
            id,
            {
                tabService: new TabServiceMock(),
                cookieUtils: undefined!,
                FrameLocator: FrameLocatorMock,
                portSelector: portSelectorMock,
                LocateElementPlayer: MobileLocateElementPlayer,
                stepActionUtils,
                stepActionFactory: this.stepActionFactory,
                playbackTimeoutCalculator: this.playbackTimeoutCalculator,
                makeSDK,
            },
        );

        this.sessionPlayer.setShouldMonitorPerformance(Boolean(shouldMonitorPerformance));
        this.sessionPlayer.playbackManager.isRemoteSession = true;
        this.sessionPlayer.playbackManager.isLocalRun = false;
        this.sessionPlayer.playbackManager.testRetryCount = testRetryCount;
        this.sessionPlayer.playbackManager.previousTestResultId = previousTestResultId;

        this.sessionPlayer.playbackManager.userParamsData = userParamsData || {};
        this.onStepCompleted = this.onStepCompleted.bind(this);

        this.sessionPlayer.playbackManager.on(commonConstants.playback.RESULT, this.onStepCompleted);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onStepCompleted(result: any, testId: string, resultId: string, step: any) {
        // One day something will happen here
    }

    async onDone() {
        const { playbackManager } = this.sessionPlayer;
        const { executionId, testResult: { resultId } = {}, appiumApi } = playbackManager;
        const logData = {
            executionId,
            testResultId: resultId,
            sessionId: appiumApi?.sessionId,
        };
        try {
            if (appiumApi?.sessionId) {
                await appiumApi?.endSession();
                logger.info('appium session deleted', { ...logData });
            }
        } catch (error) {
            logger.error('error while deleting appium session', {
                ...logData,
                error,
            });
        } finally {
            const { commonConstants } = getSessionPlayer();
            playbackManager.off(commonConstants.playback.RESULT);
        }
    }

    getSessionId() {
        return this.sessionPlayer.playbackManager.appiumApi?.sessionId;
    }
}
